<script>
import { Overlay } from '../../'
import { EventBus } from '../../EventBus'

export default {
    name: 'VTTargetsTable',
    mixins: [Overlay],
    data() {
        return {
            timeframeData: {}
        }
    },
    methods: {
        meta_info() {
            return {
                author: 'TradingCandle',
                version: '1.1.0',
                desc: 'VT Targets Table - Shows entry, target, stoploss for active timeframes',
                preset: {
                    name: 'VT Targets Table',
                    side: 'onchart',
                    settings: {
                        tableX: 10,
                        tableY: 10,
                        fontSize: 11,
                        bgColor: 'rgba(0, 0, 0, 0.75)',
                        textColor: '#e0e0e0',
                        headerColor: 'rgba(30, 40, 60, 0.95)',
                        borderColor: 'rgba(80, 90, 110, 0.5)',
                        buyColor: '#26a69a',
                        sellColor: '#ef5350',
                        showTable: true
                    }
                }
            }
        },
        init() {
            EventBus.$on('timeframes-updated', this.onTimeframesUpdated)
        },
        destroy() {
            EventBus.$off('timeframes-updated', this.onTimeframesUpdated)
        },
        onTimeframesUpdated(data) {
            if (data && typeof data === 'object') {
                this.timeframeData = data
            }
        },
        draw(ctx) {
            if (!this.show_table) return

            const layout = this.$props.layout
            const targets = this.collectTargets()
            if (!targets || targets.length === 0) return

            // Table dimensions
            const colWidths = [40, 36, 65, 65, 65] // TF, Dir, Entry, Target, SL
            const rowHeight = this.font_size + 10
            const headerHeight = rowHeight + 4
            const tableWidth = colWidths.reduce((a, b) => a + b, 0) + 12
            const tableHeight = headerHeight + (targets.length * rowHeight) + 8

            // Position at top-right of chart
            const chartWidth = layout.width || 800
            const x = chartWidth - tableWidth - 10
            const y = 10

            // Draw background
            ctx.save()
            ctx.fillStyle = this.bg_color
            ctx.fillRect(x, y, tableWidth, tableHeight)
            ctx.strokeStyle = this.border_color
            ctx.lineWidth = 0.5
            ctx.strokeRect(x, y, tableWidth, tableHeight)

            // Draw header
            ctx.fillStyle = this.header_color
            ctx.fillRect(x, y, tableWidth, headerHeight)
            ctx.strokeStyle = this.border_color
            ctx.strokeRect(x, y, tableWidth, headerHeight)

            // Header text
            ctx.fillStyle = this.text_color
            ctx.font = `bold ${this.font_size}px -apple-system, BlinkMacSystemFont, sans-serif`
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'

            const headers = ['TF', 'Dir', 'Entry', 'Target', 'SL']
            let headerX = x + 6
            headers.forEach((header, i) => {
                ctx.fillText(header, headerX + colWidths[i] / 2, y + headerHeight / 2)
                headerX += colWidths[i]
            })

            // Draw rows
            ctx.font = `${this.font_size}px -apple-system, BlinkMacSystemFont, sans-serif`
            let rowY = y + headerHeight

            targets.forEach((target, idx) => {
                // Alternating row background
                if (idx % 2 === 0) {
                    ctx.fillStyle = 'rgba(255, 255, 255, 0.03)'
                    ctx.fillRect(x + 1, rowY, tableWidth - 2, rowHeight)
                }

                let cellX = x + 6
                const dirColor = target.direction === 'BUY' ? this.buy_color
                    : target.direction === 'SELL' ? this.sell_color
                    : this.text_color

                const values = [
                    target.tf,
                    target.direction === 'BUY' ? 'L' : target.direction === 'SELL' ? 'S' : '-',
                    target.entry ? this.formatNum(target.entry) : '-',
                    target.target ? this.formatNum(target.target) : '-',
                    target.stoploss ? this.formatNum(target.stoploss) : '-'
                ]

                values.forEach((val, i) => {
                    if (i === 1) ctx.fillStyle = dirColor
                    else if (i === 3) ctx.fillStyle = this.buy_color
                    else if (i === 4) ctx.fillStyle = this.sell_color
                    else ctx.fillStyle = this.text_color
                    ctx.fillText(val, cellX + colWidths[i] / 2, rowY + rowHeight / 2)
                    cellX += colWidths[i]
                })

                // Row separator
                ctx.strokeStyle = this.border_color
                ctx.beginPath()
                ctx.moveTo(x, rowY + rowHeight)
                ctx.lineTo(x + tableWidth, rowY + rowHeight)
                ctx.stroke()

                rowY += rowHeight
            })

            ctx.restore()
        },
        collectTargets() {
            const targets = []
            const tfData = this.sett.timeframeData || this.timeframeData || {}

            const tfOrder = ['1m', '3m', '4m', '5m', '10m', '15m', '30m', '40m', '1H', '2H', '4H', '400m', '1D']
            const sortedKeys = Object.keys(tfData).sort((a, b) => {
                const aIdx = tfOrder.indexOf(a)
                const bIdx = tfOrder.indexOf(b)
                return (aIdx >= 0 ? aIdx : 99) - (bIdx >= 0 ? bIdx : 99)
            })

            for (const tf of sortedKeys) {
                const item = tfData[tf]
                if (!item || !item.entry) continue
                const entry = item.entry
                const className = String(item.className || '')
                if (className.includes('p-hidden')) continue

                let direction = ''
                let entryPrice = 0
                let targetPrice = 0
                let stoploss = 0

                if (entry.call) {
                    direction = entry.call
                    entryPrice = entry.entry || 0
                    targetPrice = entry.target || 0
                    stoploss = entry.stoploss || 0
                }

                if (!direction && entry.flashSide) {
                    direction = entry.flashSide
                    entryPrice = entry.flashEntry || 0
                    targetPrice = entry.flashTarget || 0
                    stoploss = entry.flashStoploss || 0
                }

                if (direction) {
                    targets.push({
                        tf: item.label || tf,
                        direction,
                        entry: entryPrice,
                        target: targetPrice,
                        stoploss
                    })
                }
            }
            return targets
        },
        formatNum(num) {
            if (!num || isNaN(num)) return '-'
            const n = Number(num)
            if (n >= 10000) return n.toFixed(0)
            if (n >= 100) return n.toFixed(1)
            return n.toFixed(2)
        },
        use_for() { return ['VTTargetsTable'] },
        legend() {
            return []
        }
    },
    computed: {
        sett() {
            return this.$props.settings || {}
        },
        font_size() {
            return this.sett.fontSize || 11
        },
        bg_color() {
            return this.sett.bgColor || 'rgba(0, 0, 0, 0.75)'
        },
        text_color() {
            return this.sett.textColor || '#e0e0e0'
        },
        header_color() {
            return this.sett.headerColor || 'rgba(30, 40, 60, 0.95)'
        },
        border_color() {
            return this.sett.borderColor || 'rgba(80, 90, 110, 0.5)'
        },
        buy_color() {
            return this.sett.buyColor || '#26a69a'
        },
        sell_color() {
            return this.sett.sellColor || '#ef5350'
        },
        show_table() {
            return this.sett.showTable !== false
        }
    }
}
</script>
